import { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useSetUserQuery } from 'auth/api'
import { useHeartbeat } from 'auth/hooks'
import { AuthLoginStep, IAuthorizedUser, IResponseLogin, TJwtPayload } from 'auth/models'
import { setUser } from 'auth/store'
import { createCookieWithTokenAndOriginUrl, fromPayloadToUserMapper } from 'auth/utils'
import { MoonLoader } from 'common/components'
import {
  ActionTypeEnum,
  useDispatch,
  useIsTabActive,
  useMessageActions,
  useSelector,
} from 'common/hooks'
import { canUseServiceWorkers } from 'common/utils/device.utils'
import { jwtDecode } from 'jwt-decode'
import { isNil } from 'lodash'
import { api, RoutesEnum } from 'services'

import Header from './Header'

export default function Root() {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user: currentUser, loginStep } = useSelector((state) => state.auth)
  const {
    getNavigationMessage,
    getRedirectMessage,
    getChangeTitleMessage,
    getUpdateSearchParamsMessage,
    getLogoutMessage,
    getSessionLogoutMessage,
  } = useMessageActions()

  const applicationId = useMemo(() => localStorage.getItem('application_id'), [])
  const clientId = useMemo(() => localStorage.getItem('client_id'), [])
  const siteId = useMemo(() => localStorage.getItem('site_id'), [])
  const accessToken = useMemo(() => localStorage.getItem('access_token'), [])

  const hasTokens =
    !isNil(localStorage.getItem('access_token')) &&
    !isNil(localStorage.getItem('application_url'))

  const isUser = useMemo(() => {
    return hasTokens && currentUser
  }, [hasTokens, currentUser])

  const isTabActive = useIsTabActive()

  useHeartbeat(Boolean(isUser) && isTabActive)

  useEffect(() => {
    const applicationUrl = localStorage.getItem('application_url')

    if (accessToken && applicationUrl && !currentUser) {
      try {
        const user = jwtDecode(accessToken) as TJwtPayload<IAuthorizedUser>
        // fromPayloadToUserMapper automatycznie normalizuje UTF-8
        const mappedUser = fromPayloadToUserMapper(user)
        dispatch(setUser({ user: mappedUser }))
      } catch (error) {
        console.error('Error decoding token:', error)
        // W przypadku błędu dekodowania, wyczyść tokeny
        localStorage.removeItem('access_token')
        localStorage.removeItem('application_url')
        navigate(RoutesEnum.LOGIN)
      }
    }
  }, [dispatch, currentUser, navigate, accessToken])

  const requestNotificationToken = async () => {
    if (!canUseServiceWorkers()) {
      console.log('Push notifications not supported in this environment')
      return
    }

    try {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        const [{ getToken }, { messaging }] = await Promise.all([
          import('firebase/messaging'),
          import('../../../firebaseConfig'),
        ])

        const registration = await navigator.serviceWorker.ready

        const currentToken = await getToken(messaging, {
          vapidKey:
            'BJ4auDvBdns1GwVwrCMcmLmKaiuLtElzQ5ttWwfNrhHKLTTTe3hk0vhXsLzP_F-htc3CauIHkR0NEQRalTzwSXM',
          serviceWorkerRegistration: registration,
        })
        if (currentToken) {
          await api.put(`notification/subscription`, { FcmToken: currentToken })
        }
      }
    } catch (error) {
      console.error('Error while getting token:', error)
    }
  }

  const initializeUser = async (data: IResponseLogin) => {
    try {
      const user = jwtDecode(data.access_token) as TJwtPayload<IAuthorizedUser>

      // Normalizuj język przed zmianą
      const normalizedLanguage = user.language ? user.language.normalize('NFC') : 'en'
      i18n.changeLanguage(normalizedLanguage)

      localStorage.setItem('access_token', data.access_token)
      createCookieWithTokenAndOriginUrl(data.access_token)
      await requestNotificationToken()

      // fromPayloadToUserMapper już zawiera normalizację UTF-8
      dispatch(setUser({ user: fromPayloadToUserMapper(user) }))

      // Przekieruj na zapisany URL po zalogowaniu
      const redirectUrl = localStorage.getItem('redirect_after_login')
      if (redirectUrl) {
        localStorage.removeItem('redirect_after_login')
        navigate(redirectUrl)
      }
    } catch (error) {
      console.error('Error during user initialization:', error)
      // W przypadku błędu, przekieruj na login
      navigate(RoutesEnum.LOGIN)
    }
  }

  // Clear cache before calling /set to ensure fresh token is used
  useEffect(() => {
    if (clientId && applicationId && siteId && accessToken) {
      // Clear cache to force fresh request with new token
      queryClient.removeQueries()
    }
  }, [clientId, applicationId, siteId, accessToken, queryClient])

  const { isFetching, data } = useSetUserQuery(
    {
      clientId: clientId || '',
      applicationId: applicationId || '',
      siteId: siteId || '',
    },
    {
      // Only enable if we have all IDs AND access_token (user is logged in)
      // Use state values which are updated when localStorage changes
      enabled:
        Boolean(clientId) &&
        Boolean(applicationId) &&
        Boolean(siteId) &&
        Boolean(accessToken) &&
        loginStep !== AuthLoginStep.SIGN_IN,
      staleTime: 0, // Don't cache the response
      gcTime: 0, // Remove from cache immediately
    },
  )

  useEffect(() => {
    if (data) {
      initializeUser(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    const handlePostMessage = (e: MessageEvent) => {
      if (e.data.type === ActionTypeEnum.NAVIGATION) {
        getNavigationMessage(e.data.message)
      }

      if (e.data.type === ActionTypeEnum.REDIRECT) {
        getRedirectMessage(e.data.message)
      }

      if (e.data.type === ActionTypeEnum.CHANGE_TITLE) {
        getChangeTitleMessage(e.data.message)
      }

      if (e.data.type === ActionTypeEnum.UPDATE_SEARCH_PARAMS) {
        getUpdateSearchParamsMessage(e.data.message)
      }

      if (e.data.type === ActionTypeEnum.LOGOUT) {
        getLogoutMessage(e.data.message)
      }

      if (e.data.type === ActionTypeEnum.SESSION_LOGOUT) {
        getSessionLogoutMessage()
      }
    }

    window.addEventListener('message', handlePostMessage)

    return () => {
      window.removeEventListener('message', handlePostMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isFetching) {
    return (
      <>
        <Helmet>
          <title>{t('auth.page.signing')}</title>
        </Helmet>
        <div className={'w-full h-full flex items-center justify-center bg-white'}>
          <MoonLoader />
        </div>
      </>
    )
  }

  if (isUser) {
    return (
      <>
        <Header />
        <Outlet />
      </>
    )
  }

  // Jeśli nie ma tokenów, zapisz obecny URL przed przekierowaniem na login
  if (!hasTokens) {
    const currentPath = location.pathname + location.search + location.hash
    if (
      currentPath !== RoutesEnum.LOGIN &&
      !localStorage.getItem('redirect_after_login')
    ) {
      localStorage.setItem('redirect_after_login', currentPath)
    }
    return <Navigate to={RoutesEnum.LOGIN} />
  }

  // Jeśli są tokeny ale nie ma currentUser, pokaż loader (trwa przywracanie użytkownika)
  return (
    <>
      <Helmet>
        <title>{t('auth.page.signing')}</title>
      </Helmet>
      <div className={'w-full h-full flex items-center justify-center bg-white'}>
        <MoonLoader />
      </div>
    </>
  )
}
